import { reactive } from "@vue/composition-api";
import { buildFormInventoryLotAutocomplete } from "@/models/InventoryLot";
import { buildFormSellContractAutocomplete } from "@/models/SellContract";
import { AcInventoryLotResult } from "@/components/Autocomplete/ac-inventory-lot";
import { AcSellContractResult } from "@/components/Autocomplete/ac-sell-contract";

import { ValueChanger } from "@/models/ValueChanger";

export interface IlsctlLineItemApiParams {
  average_weight_lot_unit_id: string;
  id?: string;
  il_quantity: number;
  inventory_lot_id: string;
  lot_unit_id: string;
  purchase_price: number;
  purchase_price_status: string;
  sc_quantity: number;
  sell_contract_id: string;
  sell_price: number;
  sell_price_status: string;
  status: string;
  truck_load_id?: string;
  weight: number;
  weight_lot_unit_id: string;
  weight_purchase_price: number;
  weight_purchase_price_status: string;
  weight_sell_price: number;
  weight_sell_price_status: string;
  weight_status: string;
  _destroy: boolean;
}

export const IlsctlNumberKeys: Array<keyof IlsctlLineItem> = [
  "il_quantity",
  "sc_quantity",
  "purchase_price",
  "sell_price",
  "weight",
  "weight_sell_price",
  "weight_purchase_price",
  "purchase_total",
  "weight_purchase_total",
  "sell_total",
  "weight_sell_total",
  "profit",
  "weight_profit",
  "actual_purchase_price",
  "actual_sell_price",
  "actual_profit",
  "actual_purchase_total",
  "actual_sell_total",
];

export interface IlsctlLineItem {
  average_weight_lot_unit_id: string;
  id: string;
  il_quantity: number;
  inventory_lot_id: string;
  lot_unit_id: string;
  purchase_price: number;
  purchase_price_status: string;
  sc_quantity: number;
  sell_contract_id: string;
  sell_price: number;
  sell_price_status: string;
  status: string;
  truck_load_id: string;
  weight: number;
  weight_lot_unit_id: string;
  weight_sell_price: number;
  weight_sell_price_status: string;
  weight_status: string;
  weight_purchase_price: number;
  weight_purchase_price_status: string;

  purchase_total: number;
  weight_purchase_total: number;
  sell_total: number;
  weight_sell_total: number;

  profit: number;
  weight_profit: number;

  actual_purchase_price: number;
  actual_sell_price: number;
  actual_profit: number;
  actual_purchase_total: number;
  actual_sell_total: number;

  completed_date: string;
  inventory_lot_label: string;
  lot_unit_label: string;
  sell_contract_label: string;
  truck_load_label: string;
  weight_lot_unit_label: string;
  average_weight_lot_unit_label: string;
  il_lot_unit_label: string;
  sc_lot_unit_label: string;
}

export interface FormIlsctlLineItem extends IlsctlLineItemApiParams {
  formSellContract: AcSellContractResult;
  formInventoryLot: AcInventoryLotResult;
  changerIlQuantity: ValueChanger;
  changerScQuantity: ValueChanger;

  // formLotUnit: AutocompleteResult;
}

export function buildNewIlsctlLineItem(): IlsctlLineItem {
  return {
    average_weight_lot_unit_id: "",
    id: "",
    il_quantity: 0,
    inventory_lot_id: "",
    lot_unit_id: "",
    purchase_price: 0,
    purchase_price_status: "",
    sc_quantity: 0,
    sell_contract_id: "",
    sell_price: 0,
    sell_price_status: "",
    status: "",
    truck_load_id: "",
    weight: 0,
    weight_lot_unit_id: "",
    weight_sell_price: 0,
    weight_sell_price_status: "",
    weight_purchase_price: 0,
    weight_purchase_price_status: "",
    weight_status: "",

    purchase_total: 0,
    weight_purchase_total: 0,
    sell_total: 0,
    weight_sell_total: 0,

    profit: 0,
    weight_profit: 0,

    actual_purchase_price: 0,
    actual_sell_price: 0,
    actual_profit: 0,
    actual_purchase_total: 0,
    actual_sell_total: 0,

    completed_date: "",
    inventory_lot_label: "",
    lot_unit_label: "",
    sell_contract_label: "",
    truck_load_label: "",
    weight_lot_unit_label: "",
    average_weight_lot_unit_label: "",
    il_lot_unit_label: "",
    sc_lot_unit_label: "",
  };
}

export function buildFormIlsctlLineItems() {
  let { formSellContract } = buildFormSellContractAutocomplete();
  let { formInventoryLot } = buildFormInventoryLotAutocomplete();
  // let { formLotUnit } = buildFormLotUnitAutocomplete();

  let formIlsctlLineItems = reactive<FormIlsctlLineItem[]>([]);

  let addIlsctlLineItem = (lnItems: FormIlsctlLineItem[]) => {
    lnItems.push({
      average_weight_lot_unit_id: "",
      id: "",
      il_quantity: 0,
      inventory_lot_id: "",
      lot_unit_id: "",
      purchase_price: 0,
      purchase_price_status: "estimated",
      sc_quantity: 0,
      sell_contract_id: "",
      sell_price: 0,
      sell_price_status: "estimated",
      status: "pending",
      weight: 0,
      weight_lot_unit_id: "",
      weight_purchase_price: 0,
      weight_purchase_price_status: "estimated",
      weight_sell_price: 0,
      weight_sell_price_status: "estimated",
      weight_status: "estimated",
      truck_load_id: "",
      _destroy: false,
      formSellContract: formSellContract(),
      formInventoryLot: formInventoryLot(),
      // formLotUnit: formLotUnit(),
      changerIlQuantity: ValueChanger.app,
      changerScQuantity: ValueChanger.app,
    });
  };

  let loadIlsctlLineItemIntoForm = (
    ln: IlsctlLineItem,
    fln: FormIlsctlLineItem,
  ) => {
    fln.average_weight_lot_unit_id = ln.average_weight_lot_unit_id;
    fln.id = ln.id;
    fln.sell_price = ln.sell_price;
    fln.sell_price_status = ln.sell_price_status;
    fln.truck_load_id = ln.truck_load_id;
    fln.sell_contract_id = ln.sell_contract_id;
    fln.inventory_lot_id = ln.inventory_lot_id;
    fln.status = ln.status;
    fln.il_quantity = ln.il_quantity;
    fln.sc_quantity = ln.sc_quantity;
    fln.lot_unit_id = ln.lot_unit_id;
    fln.formInventoryLot.id = ln.inventory_lot_id;
    fln.formInventoryLot.label = ln.inventory_lot_label;
    fln.weight_lot_unit_id = ln.weight_lot_unit_id;
    fln.weight_sell_price = ln.weight_sell_price;
    fln.weight_sell_price_status = ln.weight_sell_price_status;
    fln.weight_purchase_price = ln.weight_purchase_price;
    fln.weight_purchase_price_status = ln.weight_purchase_price_status;
    fln.weight = ln.weight;
    fln.weight_status = ln.weight_status;
    fln.weight_lot_unit_id = ln.weight_lot_unit_id;

    fln.changerScQuantity = ValueChanger.api;
    fln.changerIlQuantity = ValueChanger.api;
    fln.formSellContract.id = ln.sell_contract_id;
    fln.formSellContract.label = ln.sell_contract_label;
    // fln.formLotUnit.id = ln.lot_unit_id;
    // fln.formLotUnit.label = ln.lot_unit_label;
  };

  let loadIlsctlLineItemsIntoForm = (
    lns: IlsctlLineItem[],
    flns: FormIlsctlLineItem[],
  ) => {
    for (const l of lns) {
      addIlsctlLineItem(flns);
      loadIlsctlLineItemIntoForm(l, flns[flns.length - 1]);
    }
  };

  return {
    addIlsctlLineItem,
    formIlsctlLineItems,
    loadIlsctlLineItemIntoForm,
    loadIlsctlLineItemsIntoForm,
  };
}
